import EmailIcon from '@atlaskit/icon/core/email';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Modal, { ModalBody, ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { Divider, Heading, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';

import { ContactInfo } from './ContactInfo';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  description: string;
  email?: string;
  website?: string;
  logoBackgroundUrl?: string;
}

export const ContactModal = ({
  isOpen,
  onClose,
  companyName,
  description,
  email,
  website,
  logoBackgroundUrl,
}: Props) => {
  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose} width="large">
          <ModalHeader hasCloseButton />
          <ModalBody>
            <div css={contentSectionStyles}>
              <Flex alignItems="center" justifyContent="center">
                {logoBackgroundUrl && <img src={logoBackgroundUrl} alt={`${companyName} logo`} css={logoImageStyles} />}
              </Flex>
              <div css={textCenterStyles}>
                <Heading level={3}>{companyName}</Heading>
                <Text size="small" color={token('color.text.subtle')}>
                  {description}
                </Text>
              </div>
              <Divider color={token('color.border')} />
              {/* This next section might be replaced with rich text so its driven by Contentful */}
              <Stack space="space.200">
                <Text>
                  Reach out to Atlassian training partners directly in order to schedule private training using either
                  the email or website link below.
                </Text>
                <p css={headingTextStyles}>If contacting using email, please include the following information:</p>
                <ul css={bulletListStyles}>
                  <li>Name of your company</li>
                  <li>Name of the course you would like private training for</li>
                  <li>Preferred date and time for training (including your timezone)</li>
                  <li>Number of participants you expect to attend training</li>
                </ul>
              </Stack>
              <Divider color={token('color.border')} />
              <ContactInfo
                icon={<EmailIcon label="Email" color={token('color.icon')} LEGACY_size="medium" />}
                label="Email address"
                value={email}
                copyButtonLabel="Copy email"
              />
              <ContactInfo
                icon={<LinkExternalIcon label="Website" color={token('color.icon')} LEGACY_size="medium" />}
                label="Website"
                value={website}
                copyButtonLabel="Copy website"
              />
            </div>
          </ModalBody>
        </Modal>
      )}
    </ModalTransition>
  );
};

const contentSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
  padding: token('space.200'),
  paddingBottom: token('space.400'),
});

const textCenterStyles = css({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  alignItems: 'center',
});

const logoImageStyles = css({
  objectFit: 'contain',
  width: '100%',
  maxHeight: '3rem',
});

const headingTextStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: '1.5rem',
  color: token('color.text'),
});

const bulletListStyles = css({
  listStyleType: 'disc',
  paddingLeft: token('space.400'),
  marginTop: 0,
  marginBottom: 0,

  li: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: token('color.text'),
    paddingLeft: token('space.100'),
    marginTop: token('space.075'),

    '&:first-child': {
      marginTop: 0,
    },
  },
});
